import React, { useState, type KeyboardEvent } from "react";
import { EyeIcon, EyeOffIcon, Spinner } from "./Shared/Icons";
import type { useLogin } from "../hooks/useLogin";

interface LoginViewProps {
  auth: ReturnType<typeof useLogin>;
  isDark: boolean;
  onShowRegister: () => void;
  onShowRecover: () => void;
}

export const LoginView: React.FC<LoginViewProps> = ({
  auth,
  isDark,
  onShowRegister,
  onShowRecover,
}) => {
  const {
    username,
    password,
    error,
    isLoading,
    setUsername,
    setPassword,
    handleLogin,
  } = auth;

  const [showPass, setShowPass] = useState(false);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isLoading) {
      handleLogin();
    }
  };

  const inputClass =
    isDark
      ? "w-full py-4 px-5 border border-white/10 bg-white/[0.03] text-white placeholder-white/20 rounded-xl text-xs font-medium outline-none transition-all focus:border-[#058b95] focus:bg-[#121214]"
      : "w-full py-4 px-5 border border-transparent bg-[#F5F7F4] text-[#212121] placeholder-gray-400 rounded-xl text-xs font-medium outline-none transition-all focus:border-[#058b95] focus:bg-white";

  return (
    <div className="flex flex-col gap-4">
      {/* Email input */}
      <input
        type="text"
        placeholder="Usuario o Email"
        autoComplete="username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={isLoading}
        className={inputClass}
        autoFocus
      />

      {/* Password input */}
      <div className="relative">
        <input
          type={showPass ? "text" : "password"}
          placeholder="Contraseña"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={isLoading}
          className={inputClass}
        />
        <button
          type="button"
          onClick={() => setShowPass(!showPass)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#058b95] transition-all text-xs flex items-center justify-center"
        >
          {showPass ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>

      <button
        onClick={onShowRecover}
        className={`text-xs font-bold text-left self-start mt-1 cursor-pointer transition-all ${
          isDark ? "text-white/60 hover:text-white" : "text-gray-600 hover:text-[#058b95]"
        }`}
      >
        ¿Olvidaste tu contraseña?
      </button>

      <button
        onClick={handleLogin}
        disabled={isLoading}
        className="w-full py-4 bg-[#058b95] hover:bg-[#04727a] disabled:opacity-60 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-cyan-500/20 active:scale-[0.98] mt-4 flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <>
            <Spinner />
            <span>Ingresando...</span>
          </>
        ) : (
          "Ingresar"
        )}
      </button>

      <button
        type="button"
        onClick={onShowRegister}
        className={`text-xs font-bold text-center mt-3 cursor-pointer transition-all ${
          isDark ? "text-white/60 hover:text-white" : "text-gray-600 hover:text-[#058b95]"
        }`}
      >
        ¿No tenés cuenta? Registrate
      </button>
    </div>
  );
};
